import type { ContractChoice, ContractProgramFamilyOption } from '@/types/contract';

// Mirrors PhaseName in backend/homebase/programs/models/phase_name.py, in display order.
export const PHASE_TYPES: Record<string, { abbreviation: string; name: string }> = {
  outpatient: { abbreviation: 'OP', name: 'Outpatient' },
  intensive_outpatient: { abbreviation: 'IOP', name: 'Intensive Outpatient' },
  partial_hospitalization_program: {
    abbreviation: 'PHP',
    name: 'Partial Hospitalization Program'
  },
  maintenance: { abbreviation: 'Mnt', name: 'Maintenance' },
  foundational: { abbreviation: 'Fdn', name: 'Foundational' },
  on_demand: { abbreviation: 'OD', name: 'On-Demand' }
};

export const phaseTypeAbbreviation = (phaseType: string) =>
  PHASE_TYPES[phaseType]?.abbreviation ?? phaseType;

// e.g. "OP Outpatient · IOP Intensive Outpatient · ..."
export const PHASE_TYPE_LEGEND = Object.values(PHASE_TYPES)
  .map(({ abbreviation, name }) => `${abbreviation} ${name}`)
  .join(' · ');

export const familyColumnLabel = (family: ContractProgramFamilyOption) => {
  switch (family.treatment_preference) {
    case 'counseling_therapy_and_meds':
      return 'Standard';
    case 'medication_only':
      return 'Rx only';
    case 'counseling_therapy_only':
      return family.drug_category === 'mental_health' ? 'Therapy' : 'No Rx';
    default:
      return family.name;
  }
};

const CATEGORY_TAG_LABELS: Record<string, string> = {
  stimulants: 'Stimulant',
  alcohol: 'Alcohol',
  opioids: 'Opioid',
  cannabis: 'Cannabis',
  mental_health: 'MH'
};

// Short family label for tags, e.g. "Opioid Rx only" or "MH Therapy".
export const familyTagLabel = (family: ContractProgramFamilyOption) =>
  `${CATEGORY_TAG_LABELS[family.drug_category ?? ''] ?? family.name} ${familyColumnLabel(family)}`;

export interface ContractCategoryColumns {
  category: ContractChoice;
  families: ContractProgramFamilyOption[];
}

// Coverage columns in drug-category order, each with the families that have active programs.
export const groupFamiliesByCategory = (
  categories: ContractChoice[],
  families: ContractProgramFamilyOption[]
): ContractCategoryColumns[] =>
  categories
    .map((category) => ({
      category,
      families: families.filter((family) => family.drug_category === category.value)
    }))
    .filter(({ families: categoryFamilies }) => categoryFamilies.length > 0);

// Contract dates are plain dates; parsing them as UTC keeps them off the previous day.
export const formatContractMonth = (value: string) =>
  new Date(`${value}T00:00:00Z`).toLocaleDateString(undefined, {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC'
  });

export const formatContractDate = (value: string) =>
  new Date(`${value}T00:00:00Z`).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  });
